'use client';

import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LabelList } from 'recharts';
import { ActivityDataPoint, CategoryCount } from '../types';
import { getSentimentColorValue, getCategoryColor } from '../utils/styleUtils';

// Breakdown type options
type BreakdownType = 'sentiment' | 'category' | 'brand' | 'source';
// Chart type options
type ChartType = 'bar' | 'area';

interface ActivityBarChartProps {
  activityData: ActivityDataPoint[];
  maxValue: number;
  currentPeriod: string;
  brandData: CategoryCount[];
  categoryData: CategoryCount[];
  sourceData: CategoryCount[];
  breakdownType: BreakdownType;
  categoriesByPeriod?: { [period: string]: CategoryCount[] };
  brandsByPeriod?: { [period: string]: CategoryCount[] };
  sourcesByPeriod?: { [period: string]: CategoryCount[] };
  onSentimentClick?: (sentiment: string) => void;
  onCategoryClick?: (category: string) => void;
  onBrandClick?: (brand: string) => void;
  onSourceClick?: (source: string) => void;
  chartType?: ChartType;
}

export const ActivityBarChart = ({ 
  activityData, 
  maxValue, 
  currentPeriod,
  brandData,
  categoryData,
  sourceData,
  breakdownType,
  categoriesByPeriod,
  brandsByPeriod,
  sourcesByPeriod,
  onSentimentClick,
  onCategoryClick,
  onBrandClick,
  onSourceClick,
  chartType = 'bar'
}: ActivityBarChartProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (activityData.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }
  
  // Filter out periods with no data early to avoid empty charts
  const nonEmptyActivityData = activityData.filter(item => item.value > 0);
  
  if (nonEmptyActivityData.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }
  
  // Get all unique sentiment types from the data
  const allSentiments = new Set<string>();
  activityData.forEach(item => {
    Object.keys(item.sentiments).forEach(sentiment => {
      allSentiments.add(sentiment);
    });
  });

  // Get all unique categories, brands, and sources across all periods
  const allCategoriesSet = new Set<string>();
  const allBrandsSet = new Set<string>();
  const allSourcesSet = new Set<string>();
  
  // Add items from overall data
  categoryData.forEach(item => allCategoriesSet.add(item.category));
  brandData.forEach(item => allBrandsSet.add(item.category));
  sourceData.forEach(item => allSourcesSet.add(item.category));
  
  // Also add items from period-specific data if available
  if (categoriesByPeriod) {
    Object.values(categoriesByPeriod).forEach(periodCategories => {
      periodCategories.forEach(cat => allCategoriesSet.add(cat.category));
    });
  }
  
  if (brandsByPeriod) {
    Object.values(brandsByPeriod).forEach(periodBrands => {
      periodBrands.forEach(brand => allBrandsSet.add(brand.category));
    });
  }
  
  if (sourcesByPeriod) {
    Object.values(sourcesByPeriod).forEach(periodSources => {
      periodSources.forEach(source => allSourcesSet.add(source.category));
    });
  }
  
  // Convert sets to arrays
  const allCategories = Array.from(allCategoriesSet);
  const allBrands = Array.from(allBrandsSet);
  const allSources = Array.from(allSourcesSet);
  
  // Transform the data for recharts with the selected breakdown
  const chartData = activityData.map(item => {
    const result: any = {
      name: item.label,
      value: item.value,
      isCurrentPeriod: item.label === currentPeriod,
    };
    
    if (breakdownType === 'sentiment') {
      // Add sentiment breakdown - this is already included in the activity data
      allSentiments.forEach(sentiment => {
        result[sentiment] = item.sentiments[sentiment] || 0;
      });
    } else if (breakdownType === 'category') {
      // Always check if period-specific data exists for this label
      if (categoriesByPeriod && categoriesByPeriod[item.label]) {
        // Use period-specific category data
        const periodCategories = categoriesByPeriod[item.label];
        allCategories.forEach(category => {
          const categoryItem = periodCategories.find(c => c.category === category);
          result[category] = categoryItem ? categoryItem.count : 0;
        });
      } else {
        // Fallback to approximation for this period
        const categoryTotal = categoryData.reduce((sum, cat) => sum + cat.count, 0);
        categoryData.forEach(cat => {
          // Distribute the period's value proportionally across categories
          const proportion = categoryTotal > 0 ? cat.count / categoryTotal : 0;
          result[cat.category] = Math.round(item.value * proportion);
        });
      }
    } else if (breakdownType === 'brand') {
      // Always check if period-specific data exists for this label
      if (brandsByPeriod && brandsByPeriod[item.label]) {
        // Use period-specific brand data
        const periodBrands = brandsByPeriod[item.label];
        allBrands.forEach(brand => {
          const brandItem = periodBrands.find(b => b.category === brand);
          result[brand] = brandItem ? brandItem.count : 0;
        });
      } else {
        // Fallback to approximation for this period
        const brandTotal = brandData.reduce((sum, brand) => sum + brand.count, 0);
        brandData.forEach(brand => {
          // Distribute the period's value proportionally across brands
          const proportion = brandTotal > 0 ? brand.count / brandTotal : 0;
          result[brand.category] = Math.round(item.value * proportion);
        });
      }
    } else if (breakdownType === 'source') {
      // Always check if period-specific data exists for this label
      if (sourcesByPeriod && sourcesByPeriod[item.label]) {
        // Use period-specific source data
        const periodSources = sourcesByPeriod[item.label];
        allSources.forEach(source => {
          const sourceItem = periodSources.find(s => s.category === source);
          result[source] = sourceItem ? sourceItem.count : 0;
        });
      } else {
        // Fallback to approximation for this period
        const sourceTotal = sourceData.reduce((sum, source) => sum + source.count, 0);
        sourceData.forEach(source => {
          // Distribute the period's value proportionally across sources
          const proportion = sourceTotal > 0 ? source.count / sourceTotal : 0;
          result[source.category] = Math.round(item.value * proportion);
        });
      }
    }
    
    return result;
  });
  
  // Filter out periods with no data
  const filteredChartData = chartData.length > 0 ? chartData.filter(dataPoint => dataPoint.value > 0) : [];

  // Get the breakdown items for the current mode
  const getBreakdownItems = () => {
    switch (breakdownType) {
      case 'sentiment':
        return Array.from(allSentiments);
      case 'category':
        return allCategories;
      case 'brand':
        return allBrands;
      case 'source':
        return allSources;
    }
  };

  // Get color for a breakdown item
  const getItemColor = (item: string) => {
    switch (breakdownType) {
      case 'sentiment':
        return getSentimentColorValue(item);
      case 'category':
        return getCategoryColor(item, allCategories.indexOf(item));
      case 'brand':
        return getCategoryColor(item, allBrands.indexOf(item));
      case 'source':
        return getCategoryColor(item, allSources.indexOf(item));
    }
  };

  // Custom tooltip to show breakdown details for bar chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const totalValue = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
      
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-md rounded-md text-xs">
          <p className="font-medium mb-1">{label}</p>
          <p className="text-gray-700 mb-1">{`Total: ${totalValue} clip${totalValue !== 1 ? 's' : ''}`}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between mt-1">
              <div className="flex items-center">
                <div 
                  className="w-2 h-2 mr-1 rounded-sm" 
                  style={{ backgroundColor: entry.fill }}
                ></div>
                <span>{entry.dataKey}:</span>
              </div>
              <span className="ml-2">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  
  // Convert decimal to percent with optional fixed decimal places
  const toPercent = (decimal: number, fixed = 0) => `${(decimal * 100).toFixed(fixed)}%`;

  // Calculate percentage for a value based on total
  const getPercent = (value: number, total: number) => {
    const ratio = total > 0 ? value / total : 0;
    return toPercent(ratio, 1);
  };

  // Custom tooltip for area chart
  const renderAreaTooltipContent = (o: any) => {
    const { payload, label } = o;
    if (!payload || payload.length === 0) return null;
    
    const total = payload.reduce((result: number, entry: any) => result + entry.value, 0);

    return (
      <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md text-xs">
        <p className="font-medium mb-2">{`${label} (Total: ${total})`}</p>
        <ul className="list-none m-0 p-0">
          {payload.map((entry: any, index: number) => (
            <li key={`item-${index}`} className="flex items-center justify-between py-1">
              <div className="flex items-center">
                <div 
                  className="w-2 h-2 mr-1 rounded-sm" 
                  style={{ backgroundColor: entry.color }}
                />
                <span>{entry.name}:</span>
              </div>
              <span className="ml-2 font-medium">{`${entry.value} (${getPercent(entry.value, total)})`}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Shared X-axis config for both chart types
  const xAxisConfig = {
    dataKey: "name",
    tick: { fontSize: 8, fill: '#4B5563' },
    angle: -45,
    textAnchor: "end",
    height: 60,
    tickMargin: 10,
    interval: 0,
    tickFormatter: (value: string) => {
      // For mobile, try to make labels even shorter
      if (window.innerWidth < 640) {
        if (value && value.startsWith('Week ') && value.includes('(')) {
          const match = value.match(/Week \d+ \((\d+)\/(\d+)\)/);
          if (match) {
            return `${match[2]}/${match[1]}`; // Just day/month format for mobile
          }
        }
        // Shorten month names on mobile
        if (value && ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].some(month => value.includes(month))) {
          const months = {
            'January': 'Jan', 'February': 'Feb', 'March': 'Mar', 'April': 'Apr',
            'May': 'May', 'June': 'Jun', 'July': 'Jul', 'August': 'Aug',
            'September': 'Sep', 'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
          };
          Object.entries(months).forEach(([full, abbr]) => {
            value = value.replace(full, abbr);
          });
          return value;
        }
      }
      
      if (value && value.startsWith('Week ') && value.includes('(')) {
        // For weekly segmentation, just show the date in DD/MM/YY format
        const match = value.match(/Week \d+ \((\d+)\/(\d+)\)/);
        if (match) {
          const month = match[1];
          const day = match[2];
          const year = new Date().getFullYear().toString().substr(2, 2); // Get last 2 digits of current year
          return `${day}/${month}/${year}`; // Return as DD/MM/YY
        }
      }
      return value;
    }
  };

  // Shared Y-axis config
  const yAxisConfig = {
    tick: { fontSize: 8, fill: '#4B5563' },
    domain: [0, 'dataMax'] as [number, string],
    tickFormatter: (value: number) => Math.round(value).toString()
  };

  // Handle chart item click based on the breakdown type
  const handleItemClick = (item: string) => {
    switch (breakdownType) {
      case 'sentiment':
        onSentimentClick && onSentimentClick(item);
        break;
      case 'category':
        onCategoryClick && onCategoryClick(item);
        break;
      case 'brand':
        onBrandClick && onBrandClick(item);
        break;
      case 'source':
        onSourceClick && onSourceClick(item);
        break;
    }
  };

  // Render the main bar chart
  const renderBarChart = () => {
    // Ensure consistent height for all chart types
    const chartHeight = 320; // Set a fixed minimum height
    
    return (
    <ResponsiveContainer width="100%" height={chartHeight} minHeight={320}>
      <BarChart
        data={filteredChartData}
        margin={{
          top: 10,
          right: 15,
          left: -25,
          bottom: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis {...xAxisConfig} />
        <YAxis {...yAxisConfig} />
        <Tooltip content={<CustomTooltip />} />
        {isClient && window.innerWidth >= 640 && (
          <Legend 
            align="center"
            verticalAlign="bottom"
            wrapperStyle={{ 
              fontSize: '12px',
              paddingTop: 10,
              paddingBottom: 8,
              marginLeft: 40
            }} 
          />
        )}
        {getBreakdownItems().map(item => (
          <Bar 
            key={item} 
            dataKey={item} 
            stackId="a" 
            fill={getItemColor(item)} 
            name={item}
            onClick={() => handleItemClick(item)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )};

  // Render the area chart
  const renderAreaChart = () => {
    // Ensure consistent height for all chart types
    const chartHeight = 320; // Set a fixed minimum height
    
    return (
    <ResponsiveContainer width="100%" height={chartHeight} minHeight={320}>
      <AreaChart
        data={filteredChartData}
        margin={{
          top: 10,
          right: 15,
          left: -25,
          bottom: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis {...xAxisConfig} />
        <YAxis {...yAxisConfig} />
        <Tooltip content={renderAreaTooltipContent} />
        {isClient && window.innerWidth >= 640 && (
          <Legend 
            align="center"
            verticalAlign="bottom"
            wrapperStyle={{ 
              fontSize: '12px',
              paddingTop: 10,
              paddingBottom: 8,
              marginLeft: 40
            }} 
          />
        )}
        {getBreakdownItems().map(item => (
          <Area 
            key={item} 
            type="monotone" 
            dataKey={item} 
            stackId="1"
            stroke={getItemColor(item)} 
            fill={getItemColor(item)} 
            name={item}
            onClick={event => handleItemClick(item)}
            style={{ cursor: 'pointer' }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  )};

  return (
    <div className="h-full w-full relative min-h-[320px]">
      {chartType === 'bar' ? renderBarChart() : renderAreaChart()}
    </div>
  );
}; 